'use client';

import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';
import {
  MODAL_SIZES,
  MODAL_HEIGHTS,
  HEADER_CLASSES,
  FOOTER_CLASSES,
  SPACING,
  ANIMATION_DURATION,
  type ModalSize,
  type ModalHeight,
} from '@/components/shared/modal-sheet-constants';
import { cleanupPointerEvents } from '@/components/shared/utils/modalCleanup';

/**
 * BaseModal Props Interface
 *
 * Unified interface for all modal components in the application.
 * Provides consistent API on top of shadcn Dialog primitive.
 */
export interface BaseModalProps {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * Callback when open state changes
   * Called when user closes modal via X button, overlay click, or ESC key
   */
  onOpenChange: (open: boolean) => void;

  /**
   * Modal title
   * Displayed in header, used for aria-labelledby
   */
  title?: string;

  /**
   * Modal description
   * Displayed below title, used for aria-describedby
   */
  description?: string;

  /**
   * Modal size (width)
   * @default 'lg'
   */
  size?: ModalSize;

  /**
   * Modal height constraint
   * @default 'auto'
   */
  height?: ModalHeight;

  /**
   * Modal content
   */
  children: React.ReactNode;

  /**
   * Optional footer content (buttons, actions)
   */
  footer?: React.ReactNode;

  /**
   * Whether to show the close button in top-right
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Hide the standard visual header while retaining an accessible DialogTitle.
   * Useful for high-fidelity feature modals that render their own branded header.
   */
  hideHeader?: boolean;

  /** Additional classes for the scrollable content area. */
  contentClassName?: string;

  /**
   * Additional className for content container
   */
  className?: string;

  /**
   * Additional className for the overlay
   */
  overlayClassName?: string;

  /**
   * Callback fired when modal is closed (before onOpenChange)
   * Useful for cleanup operations
   */
  onClose?: () => void;

  /**
   * Prevent closing modal by clicking overlay
   * @default false
   */
  disableOverlayClose?: boolean;

  /**
   * Prevent closing modal with ESC key
   * @default false
   */
  disableEscapeClose?: boolean;

  /**
   * Prevent closing when nested modal/sheet is open
   * Pass a ref or state that tracks nested modal state
   * Based on audiences best practices for nested modal handling
   */
  nestedModalOpen?: boolean | React.RefObject<boolean>;

  /**
   * Callback for interact outside events
   * Useful for preventing parent modal from closing when nested modal is open
   */
  onInteractOutside?: (event: Event) => void;
}

/**
 * BaseModal - Unified modal component for the application
 *
 * This component provides a consistent interface on top of shadcn Dialog primitive.
 * Use this for all modal implementations to ensure consistency.
 *
 * Features:
 * - Standardized sizing (xs → 6xl)
 * - Consistent prop interface
 * - Built-in header/footer patterns
 * - Accessibility attributes
 * - Keyboard navigation (ESC, Tab)
 * - Focus management
 *
 * @example Basic usage
 * ```tsx
 * const [open, setOpen] = useState(false);
 *
 * <BaseModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Edit Profile"
 *   description="Update your profile information"
 *   size="lg"
 * >
 *   <ProfileForm />
 * </BaseModal>
 * ```
 *
 * @example With footer
 * ```tsx
 * <BaseModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Confirm Action"
 *   footer={
 *     <>
 *       <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
 *       <Button onClick={handleConfirm}>Confirm</Button>
 *     </>
 *   }
 * >
 *   <p>Are you sure you want to continue?</p>
 * </BaseModal>
 * ```
 */
export const BaseModal = React.forwardRef<HTMLDivElement, BaseModalProps>(
  (
    {
      open,
      onOpenChange,
      title,
      description,
      size = 'lg',
      height = 'auto',
      children,
      footer,
      showCloseButton = true,
      hideHeader = false,
      contentClassName,
      className,
      overlayClassName,
      onClose,
      disableOverlayClose = false,
      disableEscapeClose = false,
      nestedModalOpen = false,
      onInteractOutside,
    },
    ref
  ) => {
    // Production-safe cleanup: Remove stuck pointer-events when modal closes
    React.useEffect(() => {
      if (!open) {
        // Delay cleanup to allow animation to complete
        const timeoutId = setTimeout(() => {
          cleanupPointerEvents('BaseModal', false);
        }, ANIMATION_DURATION.modal);
        return () => clearTimeout(timeoutId);
      }
    }, [open]);

    // Check if nested modal is open (supports both boolean and ref)
    const isNestedModalOpen = React.useMemo(() => {
      if (typeof nestedModalOpen === 'boolean') {
        return nestedModalOpen;
      }
      if (nestedModalOpen && 'current' in nestedModalOpen) {
        return nestedModalOpen.current;
      }
      return false;
    }, [nestedModalOpen]);

    // Handle dialog state change
    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        // Don't close if nested modal is open
        if (!newOpen && isNestedModalOpen) {
          return;
        }

        // Call onClose callback if modal is closing
        if (!newOpen && onClose) {
          onClose();
        }
        onOpenChange(newOpen);
      },
      [onClose, onOpenChange, isNestedModalOpen]
    );

    // Handle overlay interaction
    const handleOverlayClick = React.useCallback(
      (e: Event) => {
        // Prevent closing if nested modal is open or overlay close is disabled
        if (isNestedModalOpen || disableOverlayClose) {
          e.preventDefault();
        }
        // Call custom handler if provided
        if (onInteractOutside) {
          onInteractOutside(e);
        }
      },
      [disableOverlayClose, isNestedModalOpen, onInteractOutside]
    );

    // Handle escape key
    const handleEscapeKeyDown = React.useCallback(
      (e: Event) => {
        // Prevent closing if nested modal is open or escape close is disabled
        if (isNestedModalOpen || disableEscapeClose) {
          e.preventDefault();
        }
      },
      [disableEscapeClose, isNestedModalOpen]
    );

    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          ref={ref}
          className={cn(
            MODAL_SIZES[size],
            MODAL_HEIGHTS[height],
            // Every modal must remain inside the viewport. Without a bounded
            // flex parent, the content area's overflow-y-auto never activates.
            'flex max-h-[calc(100dvh-32px)] flex-col overflow-hidden',
            className
          )}
          overlayClassName={overlayClassName}
          showCloseButton={showCloseButton}
          onPointerDownOutside={handleOverlayClick}
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          {/* Always include DialogTitle for accessibility, even with a custom visual header. */}
          {hideHeader ? (
            <>
              <VisuallyHidden asChild>
                <DialogTitle>{title || 'Dialog'}</DialogTitle>
              </VisuallyHidden>
              {description && (
                <VisuallyHidden asChild>
                  <DialogDescription>{description}</DialogDescription>
                </VisuallyHidden>
              )}
            </>
          ) : (
            <DialogHeader className={cn(HEADER_CLASSES.modal, 'shrink-0')}>
              {title ? (
                <DialogTitle>{title}</DialogTitle>
              ) : (
                <VisuallyHidden asChild>
                  <DialogTitle>Dialog</DialogTitle>
                </VisuallyHidden>
              )}
              {description && <DialogDescription>{description}</DialogDescription>}
            </DialogHeader>
          )}

          {/* Content */}
          <div className={cn(
            'min-h-0 flex-1 overscroll-contain overflow-y-auto py-4',
            !hideHeader && !title && !description && SPACING.modalPadding,
            contentClassName,
          )}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <DialogFooter className={cn(FOOTER_CLASSES.modal, 'shrink-0')}>
              {footer}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    );
  }
);

BaseModal.displayName = 'BaseModal';
